import Header from '../../components/feature/Header';
import SecurityAuditForm from '../../components/feature/SecurityAuditForm';
import TrustBadges from '../../components/feature/TrustBadges';
import Footer from '../../components/feature/Footer';

export default function Home() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-28">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Get Your <span className="text-orange-500">FREE</span> Security Audit in 3 Simple Steps
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our security experts will assess your home's vulnerabilities and provide personalized 
            recommendations. The process takes less than 2 minutes to complete.
          </p>
        </div>

        {/* Value Props Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 w-full max-w-2xl mx-auto">
          <div className="bg-white border border-green-200 rounded-xl px-5 py-4 flex items-start space-x-3">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-green-500 text-xl">
              <i className="ri-gift-line"></i>
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm leading-snug">Free Home Security Assessment</p>
              <p className="text-xs text-green-600 font-medium mt-0.5">Limited Spots Today</p>
            </div>
          </div>

          <div className="bg-white border border-orange-200 rounded-xl px-5 py-4 flex items-start space-x-3">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-orange-500 text-xl">
              <i className="ri-calendar-check-line"></i>
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm leading-snug">Installation Available</p>
              <p className="text-xs text-orange-600 font-medium mt-0.5">This Week</p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-start space-x-3">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-gray-500 text-xl">
              <i className="ri-timer-line"></i>
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm leading-snug">No Obligation</p>
              <p className="text-xs text-gray-500 font-medium mt-0.5">Takes 60 Seconds</p>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="flex justify-center mb-12">
          <SecurityAuditForm />
        </div>

        {/* Trusted by 50,000+ Homeowners */}
        <div className="bg-white rounded-xl shadow-lg p-8 text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Trusted by 50,000+ Homeowners
          </h2>
          <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-12">
            <div className="flex items-center space-x-2">
              <div className="flex text-yellow-400">
                <i className="ri-star-fill text-xl"></i>
                <i className="ri-star-fill text-xl"></i>
                <i className="ri-star-fill text-xl"></i>
                <i className="ri-star-fill text-xl"></i>
                <i className="ri-star-fill text-xl"></i>
              </div>
              <div className="text-left">
                <div className="text-2xl font-bold text-gray-900">4.9/5</div>
                <div className="text-sm text-gray-600">(2,847 reviews)</div>
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">50,000+</div>
              <div className="text-sm text-gray-600">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">15+</div>
              <div className="text-sm text-gray-600">Years Experience</div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <TrustBadges />

        {/* Why Choose Section with 5-Point Audit */}
        <div className="mt-16 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Why Choose Our Professional Home Security Audit?
          </h2>
          
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Why Choose Benefits */}
            <div>
              {/* BBB Rating and Experience */}
              <div className="flex items-center space-x-2 mb-8">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-line text-white text-xs"></i>
                </div>
                <span className="text-lg font-semibold text-gray-900">BBB A+ Rated • 15+ Years Experience</span>
              </div>
              
              <div className="mb-8">
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  We've protected over 50,000 homes by identifying vulnerabilities that burglars exploit. Our certified experts provide personalized protection strategies based on your specific needs and budget.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-orange-500 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No obligation consultation</h3>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-star-line text-orange-500 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert security assessment</h3>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-orange-500 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Personalized recommendations</h3>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-orange-500 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Same-day response available</h3>
                  </div>
                </div>
              </div>
              
              {/* Call to Action */}
              <div className="mt-8 bg-orange-50 border border-orange-200 rounded-lg p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-3">Connect With a Home Safety Expert</h4>
                <div className="flex items-center space-x-3 mb-3">
                  <button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 whitespace-nowrap cursor-pointer">
                    <i className="ri-phone-line"></i>
                    <span>Call Now for Instant Quote: 7027601474</span>
                  </button>
                  <span className="text-gray-600">Call now for immediate assistance</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-2 sm:space-y-0 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <div className="flex text-yellow-400">
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                      <i className="ri-star-fill"></i>
                    </div>
                    <span>4.9/5 (2,847 reviews)</span>
                  </div>
                  <div>50,000+ homes protected</div>
                  <div>24/7 monitoring available</div>
                </div>
              </div>
            </div>

            {/* Right Column - 5-Point Security Audit */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Your 5-Point Security Audit Includes:</h4>
              <div className="space-y-6">
                <div className="flex items-start space-x-4 bg-orange-50 p-4 rounded-lg">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                    1
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2">Entry Point Analysis</h5>
                    <p className="text-sm text-gray-600">Doors, windows, and vulnerable access points</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-blue-50 p-4 rounded-lg">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                    2
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2">Surveillance Blind Spots</h5>
                    <p className="text-sm text-gray-600">Areas where intruders can approach undetected</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-green-50 p-4 rounded-lg">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                    3
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2">Lighting & Visibility</h5>
                    <p className="text-sm text-gray-600">Strategic placement for maximum deterrence</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-purple-50 p-4 rounded-lg">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                    4
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2">Smart Home Integration</h5>
                    <p className="text-sm text-gray-600">Connecting security with home automation</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-red-50 p-4 rounded-lg">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                    5
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2">Budget-Friendly Solutions</h5>
                    <p className="text-sm text-gray-600">Prioritized recommendations within your budget</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certified & Trusted Section */}
        <div className="p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Certified & Trusted</h2>
          <div className="flex flex-wrap justify-center items-center gap-8">
            <div className="flex items-center space-x-2 border border-gray-300 rounded-lg px-4 py-2">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <i className="ri-shield-check-line text-white"></i>
              </div>
              <span className="font-semibold text-gray-900">BBB A+ Rating</span>
            </div>
            <div className="flex items-center space-x-2 border border-gray-300 rounded-lg px-4 py-2">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <i className="ri-fire-line text-white"></i>
              </div>
              <span className="font-semibold text-gray-900">NFPA Certified</span>
            </div>
            <div className="flex items-center space-x-2 border border-gray-300 rounded-lg px-4 py-2">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-white"></i>
              </div>
              <span className="font-semibold text-gray-900">UL Listed Equipment</span>
            </div>
            <div className="flex items-center space-x-2 border border-gray-300 rounded-lg px-4 py-2">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                <i className="ri-award-line text-white"></i>
              </div>
              <span className="font-semibold text-gray-900">Licensed & Insured</span>
            </div>
            <div className="flex items-center space-x-2 border border-gray-300 rounded-lg px-4 py-2">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                <i className="ri-time-line text-white"></i>
              </div>
              <span className="font-semibold text-gray-900">24/7 Monitoring</span>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mt-16 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            What Our Customers Say
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex text-yellow-400 mb-4">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
              <p className="text-gray-600 mb-4">
                "The security audit was incredibly thorough. They identified vulnerabilities I never would have noticed and provided practical solutions that fit our budget perfectly."
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">SM</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Sarah Mitchell</div>
                  <div className="text-sm text-gray-500">Phoenix, AZ</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex text-yellow-400 mb-4">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
              <p className="text-gray-600 mb-4">
                "Professional, knowledgeable, and truly free! No sales pressure whatsoever. They gave us actionable recommendations that made our home much safer."
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">MJ</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Michael Johnson</div>
                  <div className="text-sm text-gray-500">Denver, CO</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex text-yellow-400 mb-4">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
              <p className="text-gray-600 mb-4">
                "Amazing service! They completed the audit in just 30 minutes and provided a detailed report the same day. Highly recommend to any homeowner."
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">LR</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Lisa Rodriguez</div>
                  <div className="text-sm text-gray-500">Austin, TX</div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </main>

      <Footer />
    </div>
  );
}
